import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { storage } from '@abyss/web/tools/storage';
import { Drawer } from '@abyss/web/ui/Drawer';
import { Flex } from '@abyss/web/ui/Flex';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Popover } from '@abyss/web/ui/Popover';
import { Text } from '@abyss/web/ui/Text';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { Constants } from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { ConstantsRoutes } from '../../../../common/ConstantsRoutes';
import { convertObjectToUrlParams } from '../../../../common/Utils';
import { isEniUspMember } from '../../../../common/Utils/memberUtils/memberUtils';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag/useFeatureFlag';
import { useLagoon } from '../../../../hooks/useLagoon';
import { useChipStore } from '../../../../store/useChipStore';
import { ChipState } from '../../../../store/useChipStore/chipStore';
import { useDetailsStore } from '../../../../store/useDetailsStore';
import { DetailsStore } from '../../../../store/useDetailsStore/detailsStore';
import { usePCPStore } from '../../../../store/usePCPStore';
import { PCPSearchStore } from '../../../../store/usePCPStore/pcpStore';
import { useTypeaheadStore } from '../../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../../store/useTypeaheadStore/typeaheadStore';
import { getStateFromSearchBar } from '../../../../utils/locationSelectors';
import { getYesNoNAText } from '../../../../utils/providerDetails.utils';
import {
  capitalizeAllFirstCharacters,
  normalizeMedicalGroupName,
} from '../../../../utils/providerNames.utils';
import { isTruthy } from '../../../../utils/string.utils';
import { getCurrentMember } from '../../../../utils/user.utils';
import { phoneOnly } from '../../../ConstantsStyles';
import { LinkWithTracking } from '../../../LinkWithTracking/LinkWithTracking';
import { CopyToClipBoard } from '../CopyToClipBoard';
import { NetworkAffiliation } from './NetworkAffiliationsCard';
import {
  acceptingRow,
  acceptingText as acceptingTextStyle,
  affiliationDivider,
  affiliationRowContainer,
  affiliationTitleText,
  drawerBody,
  drawerContentContainer,
  enrollmentIdRow,
  enrollmentIdText,
  infoIconCss,
  infoPopoverContentText,
  infoPopoverCss,
  searchAllLabelSpan,
  searchAllLinkCss,
  searchAllNameSpan,
  sectionContainer,
  sectionContainerLarge,
  sectionHeadingRow,
  seeMoreToggleLink,
  subAttributeIndentRow,
  subAttributeText,
  subAttributeTextIndented,
  subAttributeUnavailableTextIndented,
} from './NetworkAffiliationsSideDrawer.style';

const SPECIALTIES_COLLAPSE_THRESHOLD = 3;
const MEDICAL_GROUPS_COLLAPSE_THRESHOLD = 3;

type DrawerProps = {
  affiliations: NetworkAffiliation[];
  isOpen: boolean;
  onClose: () => void;
  pcpId?: string;
  selectedAffiliationId?: string;
  coverageType?: string;
  onSearchAll?: (affiliation: NetworkAffiliation) => void;
  providerType?: string;
  isBehavioralHealth?: boolean;
};

type RowProps = {
  affiliation: NetworkAffiliation;
  onSearchAll?: (affiliation: NetworkAffiliation) => void;
  t: (key: string) => string;
  infoMessage: string | undefined;
  index: number;
  pcpId?: string;
  providerType?: string;
  isBehavioralHealth?: boolean;
};

export const getLagoonInfoDescription = (
  lagoonDescriptions: any,
  state: string,
  coverageType: string,
  typeCode: string
): string | undefined =>
  lagoonDescriptions.find(
    (desc: any) =>
      desc.state === state &&
      desc.coverage === coverageType &&
      desc.entityType === typeCode
  )?.description || '';

const getSectionVisibility = (
  providerType?: string,
  isBehavioralHealth?: boolean
) => {
  const [disableNetworkAffiliationANPEnhancements] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS
      .DISABLE_NETWORK_AFFILIATION_ACCEPTING_NEW_PATIENTS_ENHANCEMENTS,
  ]);
  const isOrganization = providerType?.toLowerCase() === 'organization';
  const showANP = !(isOrganization && disableNetworkAffiliationANPEnhancements);
  const isPractitioner = providerType?.toLowerCase() === 'practitioner';
  const isGroup =
    providerType === 'Medical Group' || providerType === 'Provider groups';

  if (isGroup) {
    return {
      accepting: false,
      acceptingConditional: false,
      enrollmentId: true,
      specialties: false,
      medicalGroups: false,
      pcpServices: false,
    };
  }
  if (isPractitioner && isBehavioralHealth) {
    // BH Provider
    return {
      accepting: true,
      acceptingConditional: false,
      enrollmentId: false,
      specialties: true,
      medicalGroups: true,
      pcpServices: false,
    };
  }
  if (isOrganization && isBehavioralHealth) {
    // BH Facility
    return {
      accepting: showANP,
      acceptingConditional: showANP,
      enrollmentId: false,
      specialties: true,
      medicalGroups: true,
      pcpServices: false,
    };
  }
  if (isOrganization && !isBehavioralHealth) {
    // Facility
    return {
      accepting: showANP,
      acceptingConditional: showANP,
      enrollmentId: true,
      specialties: true,
      medicalGroups: true,
      pcpServices: false,
    };
  }
  if (isPractitioner && !isBehavioralHealth) {
    // Medical Provider
    return {
      accepting: true,
      acceptingConditional: false,
      enrollmentId: true,
      specialties: true,
      medicalGroups: true,
      pcpServices: true,
    };
  }
  // Default — show all
  return {
    accepting: true,
    acceptingConditional: false,
    enrollmentId: true,
    specialties: true,
    medicalGroups: true,
    pcpServices: true,
  };
};

const AffiliationRow = ({
  affiliation,
  onSearchAll,
  t,
  infoMessage,
  index,
  providerType,
  isBehavioralHealth,
}: RowProps) => {
  const [isInfoPopoverOpen, setIsInfoPopoverOpen] = useState(false);
  const [specialtiesExpanded, setSpecialtiesExpanded] = useState(false);
  const [medicalGroupExpanded, setMedicalGroupExpanded] = useState(false);

  const { lineOfBusiness, isEniNiceMember, population } = getCurrentMember();
  const isEni = isEniNiceMember || isEniUspMember(lineOfBusiness, population);

  const vis = getSectionVisibility(providerType, isBehavioralHealth);
  const showAccepting =
    vis.accepting &&
    (!vis.acceptingConditional ||
      (affiliation.acceptingNewPatients !== undefined &&
        affiliation.acceptingNewPatients !== null));

  const affiliationName = capitalizeAllFirstCharacters(
    affiliation.networkAffiliationName?.toLowerCase() || ''
  );

  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { setTypeaheadSearchStore } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      setTypeaheadSearchStore: state.setTypeaheadSearchStore,
    }))
  );

  const { setDetailsStore } = useDetailsStore(
    useShallow((state: DetailsStore) => ({
      setDetailsStore: state.setDetailsStore,
    }))
  );

  const { choosePCP } = usePCPStore(
    useShallow((state: PCPSearchStore) => ({
      choosePCP: state.pcpSearchState.choosePCP,
    }))
  );

  const handleRedirectToProviderGroupDetails = (providerGroupClicked) => {
    const typeaheadStoreValues = {
      sectionType: Constants.RESULT_SECTION.PROVIDER_GROUPS,
    };
    const detailsStoreValues = {
      providerId: providerGroupClicked.id,
    };
    setTypeaheadSearchStore(typeaheadStoreValues);
    setDetailsStore(detailsStoreValues);

    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.SELECTED_PROVIDER_ID,
      providerGroupClicked.id
    );
    const urlParams = convertObjectToUrlParams(chipStore, {
      ...detailsStoreValues,
      ...typeaheadStoreValues,
    });
    window.open(
      `/findcare${ConstantsRoutes.PROVIDER_GROUP_DETAILS.path}${urlParams}`,
      '_blank'
    );
  };
  const specialities = affiliation.specialities ?? [];
  const visibleSpecialties = specialtiesExpanded
    ? specialities
    : specialities.slice(0, SPECIALTIES_COLLAPSE_THRESHOLD);
  const hasMoreSpecialties =
    specialities.length > SPECIALTIES_COLLAPSE_THRESHOLD;

  const medicalGroupNames = affiliation.medicalgrpaffilIdName
    ? (affiliation.medicalgrpaffilIdName as unknown as string[]).map(
        (name: string) => normalizeMedicalGroupName(name?.toLowerCase())
      )
    : [];

  const enrollmentId = affiliation.pcpId || '';
  const pcpIndicator = affiliation.pcpIndicator;

  const acceptingIcon = affiliation.acceptingNewPatients ? 'done' : 'close';
  const acceptingText = affiliation.acceptingNewPatients
    ? t('Accepting new patients')
    : t('Not accepting new patients');

  return (
    <Flex css={affiliationRowContainer} direction="column">
      {/* 1. Title + info popover icon */}
      <Flex alignItems="center" css={sectionHeadingRow}>
        <Text css={affiliationTitleText}>
          <span
            data-testid={`network-affiliation-side-drawer-card-title`}
            title={affiliationName}
          >
            {affiliationName}
          </span>
        </Text>
        {infoMessage && !isEni && (
          <Popover
            align="start"
            aria-label={`${t('INFORMATION_ABOUT')} ${affiliationName}`}
            content={<Text css={infoPopoverContentText}>{infoMessage}</Text>}
            css={infoPopoverCss}
            data-testid="info-popover-content-text"
            onOpenChange={() => setIsInfoPopoverOpen(!isInfoPopoverOpen)}
            open={isInfoPopoverOpen}
            width={300}
          >
            <IconMaterial
              css={infoIconCss}
              data-testid={`network-affiliation-info-icon-${index}`}
              icon="info"
              onClick={() => setIsInfoPopoverOpen(true)}
              size={16}
              variant="outlined"
            />
          </Popover>
        )}
      </Flex>

      {/* 2. Accepting / Not accepting new patients */}
      {showAccepting && (
        <Flex alignItems="center" css={acceptingRow}>
          <IconMaterial color="$primary1" icon={acceptingIcon} size={20} />
          <Text
            css={acceptingTextStyle}
            data-testid={`network-affiliation-accepting-new-patients-status`}
            fontWeight="$semibold"
          >
            {acceptingText}
          </Text>
        </Flex>
      )}

      {/* 3. Enrollment ID */}
      {vis.enrollmentId && (
        <Flex css={sectionContainer} direction="column">
          <Flex alignItems="center" css={sectionHeadingRow}>
            <IconMaterial color="$primary1" icon="person" size={20} />
            <Text
              data-testid={`network-affiliation-enrollment-id-label`}
              fontWeight="$semibold"
            >
              {isTruthy(pcpIndicator)
                ? t('NETWORK_AFFILIATION.ENROLLMENT_ID')
                : t('PROVIDER_LOCATIONS_DETAILS.PROVIDER_ID')}
            </Text>
          </Flex>
          <Flex alignItems="center" css={enrollmentIdRow}>
            <Text css={enrollmentIdText}>
              {enrollmentId ||
                t('PROVIDER_LOCATIONS_DETAILS.NOT_AVAILABLE_UNKNOWN')}
            </Text>
            {enrollmentId && (
              <CopyToClipBoard
                gap="0"
                iconSize={20}
                label={
                  isTruthy(pcpIndicator)
                    ? t('NETWORK_AFFILIATION.COPY_ENROLLMENT_ID')
                    : t(
                        'PROVIDER_LOCATIONS_DETAILS.COPY_PROVIDER_ID_TO_CLIPBOARD'
                      )
                }
                text={enrollmentId}
                title="enrollment-id"
              />
            )}
          </Flex>
        </Flex>
      )}

      {/* 4. Specialties */}
      {vis.specialties && (
        <Flex css={sectionContainerLarge} direction="column">
          <Flex alignItems="center" css={sectionHeadingRow}>
            <IconSymbol color="$primary1" icon="inventory" size={20} />
            <Text
              data-testid={`network-affiliation-side-drawer-specialties-label`}
              fontWeight="$semibold"
            >
              {t('PROVIDER_LOCATIONS_DETAILS.SPECIALTIES')}
            </Text>
          </Flex>
          {specialities.length === 0 ? (
            <Text css={subAttributeUnavailableTextIndented}>
              {t('PROVIDER_LOCATIONS_DETAILS.NOT_AVAILABLE_UNKNOWN')}
            </Text>
          ) : (
            <Flex css={subAttributeIndentRow()} direction="column">
              {visibleSpecialties.map((spec) => (
                <Text css={subAttributeText} key={spec.id}>
                  {capitalizeAllFirstCharacters(
                    spec.value?.toLowerCase() || ''
                  )}
                </Text>
              ))}
              {hasMoreSpecialties && (
                <LinkWithTracking
                  analyticsInfo={{
                    name: specialtiesExpanded
                      ? 'see less specialties'
                      : 'see more specialties',
                    location: 'body:network-affiliations-drawer',
                  }}
                  css={seeMoreToggleLink}
                  data-auto-testid={`specialties-toggle-${index}`}
                  data-testid={`specialties-toggle-${index}`}
                  isStandardAnchor
                  onClick={() => setSpecialtiesExpanded(!specialtiesExpanded)}
                >
                  {specialtiesExpanded
                    ? t('CONTENT_FILTERS.SHOW_LESS')
                    : `${t('CONTENT_FILTERS.TOGGLE_EXPAND_SHOW_MORE')} (${
                        specialities.length - SPECIALTIES_COLLAPSE_THRESHOLD
                      })`}
                </LinkWithTracking>
              )}
            </Flex>
          )}
        </Flex>
      )}

      {/* 5. PCP Services Available — medical providers only, if value exists */}
      {vis.pcpServices &&
        affiliation.pcpIndicator !== undefined &&
        affiliation.pcpIndicator !== null && (
          <Flex css={sectionContainer} direction="column">
            <Flex alignItems="center" css={sectionHeadingRow}>
              <IconMaterial
                color="$primary1"
                icon="local_hospital"
                size={20}
                variant="outlined"
              />
              <Text
                data-testid={`network-affiliation-side-drawer-pcp-services-available-label`}
                fontWeight="$semibold"
              >
                {t('PROVIDER_LOCATIONS_DETAILS.PCP_SERVICES_AVAILABLE')}
              </Text>
            </Flex>
            <Text css={subAttributeTextIndented}>
              {getYesNoNAText(
                t,
                affiliation.pcpIndicator == null
                  ? undefined
                  : String(affiliation.pcpIndicator).toLowerCase() === 'true'
              ) ?? t('PROVIDER_LOCATIONS_DETAILS.NOT_AVAILABLE_UNKNOWN')}
            </Text>
          </Flex>
        )}

      {/* 6. Medical Group */}
      {vis.medicalGroups && (
        <Flex css={sectionContainer} direction="column">
          <Flex alignItems="center" css={sectionHeadingRow}>
            <IconMaterial color="$primary1" icon="groups" size={20} />
            <Text
              data-testid={`network-affiliation-side-drawer-medical-group-label`}
              fontWeight="$semibold"
            >
              {t('NETWORK_AFFILIATION.MEDICAL_GROUP')}
            </Text>
          </Flex>
          {medicalGroupNames.length > 0 ? (
            <Flex css={subAttributeIndentRow()} direction="column">
              {(medicalGroupExpanded
                ? medicalGroupNames
                : medicalGroupNames.slice(0, MEDICAL_GROUPS_COLLAPSE_THRESHOLD)
              ).map((groups, i) => (
                <Flex alignItems="center" key={i}>
                  {groups?.id?.toLowerCase() !== 'nonpar' ? (
                    <LinkWithTracking
                      analyticsInfo={{
                        name: groups.value,
                        location: `body:${Constants.PROVIDER_DETAILS.LOCATIONS_TAB.GROUP_AFFILIATION}`,
                      }}
                      css={{ fontWeight: 600, fontSize: '14px' }}
                      data-auto-testid="medical-group-name-with-link"
                      data-testid="medical-group-name-with-link"
                      isStandardAnchor
                      onClick={() =>
                        handleRedirectToProviderGroupDetails(groups)
                      }
                    >
                      {groups.value}
                      <IconMaterial
                        css={{
                          color: '#196ECF',
                          marginLeft: '4px',
                          fontWeight: 600,
                        }}
                        icon="open_in_new"
                        size={14}
                      />
                    </LinkWithTracking>
                  ) : (
                    <Text
                      css={{ fontWeight: 400, fontSize: '14px' }}
                      data-auto-testid="medical-group-name-without-link"
                      data-testid="medical-group-name-without-link"
                    >
                      {groups.value}
                    </Text>
                  )}
                </Flex>
              ))}
              {medicalGroupNames.length > MEDICAL_GROUPS_COLLAPSE_THRESHOLD && (
                <LinkWithTracking
                  analyticsInfo={{
                    name: medicalGroupExpanded
                      ? 'see less medical groups'
                      : 'see more medical groups',
                    location: 'body:network-affiliations-drawer',
                  }}
                  data-auto-testid={`medical-group-toggle-${index}`}
                  data-testid={`medical-group-toggle-${index}`}
                  isStandardAnchor
                  onClick={() => setMedicalGroupExpanded(!medicalGroupExpanded)}
                >
                  <Flex css={{ alignItems: 'center', gap: '4px' }}>
                    {!medicalGroupExpanded && (
                      <IconSymbol icon="add" size={16} />
                    )}
                    <Text color="#196ECF" fontWeight={600} size={12}>
                      {medicalGroupExpanded
                        ? t('CONTENT_FILTERS.SHOW_LESS')
                        : `${
                            medicalGroupNames.length -
                            MEDICAL_GROUPS_COLLAPSE_THRESHOLD
                          } ${t('NETWORK_AFFILIATION.MORE')}`}
                    </Text>
                  </Flex>
                </LinkWithTracking>
              )}
            </Flex>
          ) : (
            <Text
              css={subAttributeUnavailableTextIndented}
              data-auto-testid="medical-group-name-not-available"
              data-testid="medical-group-name-not-available"
            >
              {t('PROVIDER_LOCATIONS_DETAILS.NOT_AVAILABLE_UNKNOWN')}
            </Text>
          )}
        </Flex>
      )}

      {/* 7. Patient age and gender requirements — removed (not shown for any provider type) */}

      {/* 8. Search for all providers in {name}
        - Display link only if users is not in PCP search flow US10495660
       */}
      {!choosePCP && (
        <LinkWithTracking
          analyticsInfo={{
            name: `search all providers in ${affiliationName}`,
            location: 'body:network-affiliations-drawer',
          }}
          css={searchAllLinkCss}
          data-auto-testid={`search-all-providers-${index}`}
          data-testid={`search-all-providers-${index}`}
          isStandardAnchor
          onClick={() => onSearchAll?.(affiliation)}
        >
          <Text css={searchAllLabelSpan}>
            {t('NETWORK_AFFILIATION.SEARCH_ALL_PROVIDERS_IN')}
            <span style={searchAllNameSpan}>{affiliationName}</span>
          </Text>
        </LinkWithTracking>
      )}
    </Flex>
  );
};

export const NetworkAffiliationsSideDrawer = ({
  affiliations,
  selectedAffiliationId = '',
  isOpen,
  onClose,
  pcpId,
  onSearchAll,
  providerType,
  coverageType,
  isBehavioralHealth,
}: DrawerProps) => {
  const { t } = useTranslation();
  const mobileScreen = useMediaQuery(phoneOnly);

  const lagoonInfoDescriptions = useLagoon(
    'network-affiliations-descriptions'
  )();

  const sortedAffiliations = [...affiliations].sort((a, b) =>
    a.networkAffiliationName.localeCompare(b.networkAffiliationName)
  );

  return (
    <Drawer
      css={{
        '.abyss-modal-base-content-container':
          drawerContentContainer(mobileScreen),
        '.abyss-modal-base-body': drawerBody,
      }}
      data-auto-testid="network-affiliations-side-drawer"
      data-testid="network-affiliations-side-drawer"
      isOpen={isOpen}
      onClose={onClose}
      position={mobileScreen ? 'bottom' : 'right'}
      size={mobileScreen ? window.innerHeight - 15 : 492}
      title={`${t('CONTENT_FILTERS.NETWORK_AFFILIATIONS')} (${
        sortedAffiliations.length
      })`}
    >
      <Flex direction="column">
        {sortedAffiliations.length === 0 ? (
          <Text css={{ paddingLeft: '20px' }}>
            {t('NETWORK_AFFILIATION.NO_NETWORK_AFFILIATIONS_AVAILABLE')}
          </Text>
        ) : (
          sortedAffiliations.map((affiliation, index) => (
            <div
              data-auto-testid={`network-affiliation-side-drawer-card`}
              data-testid={`network-affiliation-side-drawer-card`}
              key={`${affiliation.networkAffiliationId}-${index}`}
              ref={(el) => {
                if (
                  el &&
                  selectedAffiliationId !== '' &&
                  affiliation.networkAffiliationId + index ===
                    selectedAffiliationId
                ) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
            >
              <React.Fragment>
                <div style={affiliationDivider} />
                <AffiliationRow
                  affiliation={affiliation}
                  index={index}
                  infoMessage={getLagoonInfoDescription(
                    lagoonInfoDescriptions,
                    getStateFromSearchBar() || '',
                    coverageType || '',
                    affiliation.networkAffiliationTypeCode || ''
                  )}
                  isBehavioralHealth={isBehavioralHealth}
                  onSearchAll={onSearchAll}
                  pcpId={pcpId}
                  providerType={providerType}
                  t={t}
                />
              </React.Fragment>
            </div>
          ))
        )}
      </Flex>
    </Drawer>
  );
};
